import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, ArrowRight, Briefcase, Users, Star, Building2, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useI18n } from "../lib/i18n";
import { API } from "../lib/auth";
import JobCard from "../components/JobCard";
import StatsSection from "../components/StatsSection";
import CVMatcher from "../components/CVMatcher";
import { useSeo } from "../lib/useSeo";

const HERO_BG = "https://images.unsplash.com/photo-1758691737467-fe12934ddc58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwcHJvZmVzc2lvbmFscyUyMHdvcmtpbmclMjBsYXB0b3AlMjBvZmZpY2V8ZW58MHx8fHwxNzc5Mzc4NTQwfDA&ixlib=rb-4.1.0&q=85";
const EMP_BG = "https://images.pexels.com/photos/36733328/pexels-photo-36733328.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function HomePage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [k, setK] = useState("");
  const [latest, setLatest] = useState([]);

  useSeo({
    title: lang === "fr" ? "Trouvez un emploi à Maurice — Motravay" : "Find Jobs in Mauritius — Motravay",
    description: lang === "fr"
      ? "Motravay — Le 1er job board de Maurice. Trouvez un emploi à Maurice : Port-Louis, Ebène, Curepipe, Quatre-Bornes, Rose-Hill, Grand Baie. Postulez en ligne avec ou sans CV."
      : "Motravay — The #1 job board in Mauritius. Find jobs in Port-Louis, Ebène, Curepipe, Quatre-Bornes, Rose-Hill, Grand Baie. Apply online with or without CV.",
    canonical: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Motravay",
      "url": "https://motravay.mu",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://motravay.mu/jobs?k={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
  });

  useEffect(() => {
    axios.get(`${API}/jobs?limit=6`).then(r => setLatest(r.data.jobs || []));
  }, []);

  const search = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (k) params.set("k", k);
    params.set("l", "Mauritius");
    navigate(`/jobs?${params.toString()}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-white" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-[#1A8FE3] font-semibold mb-4">
              <MapPin className="h-3 w-3" />Motravay · Mauritius
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05]">
              {t("hero.title")}
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl mx-auto">
              {lang === "fr" ? "Des centaines d'offres d'emploi à Maurice." : "Hundreds of opportunities across Mauritius."}
            </p>
          </div>

          <form onSubmit={search} data-testid="hero-search-form" className="mt-10 max-w-2xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-lg p-2 flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex items-center gap-2 px-3">
              <Search className="h-5 w-5 text-slate-400 shrink-0" />
              <Input
                data-testid="hero-search-keyword"
                value={k}
                onChange={(e) => setK(e.target.value)}
                placeholder={lang === "fr" ? "Titre, mot-clé, entreprise" : "Title, keyword, company"}
                className="border-0 focus-visible:ring-0 shadow-none text-base h-12"
              />
            </div>
            <Button type="submit" data-testid="hero-search-submit" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white h-12 px-6 rounded-xl">
              {t("hero.cta")}<ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Button
              variant="outline"
              onClick={() => navigate("/jobs")}
              data-testid="hero-view-all-jobs"
              className="rounded-full border-slate-300 text-slate-700 hover:border-[#1A8FE3] hover:text-[#1A8FE3] hover:bg-white h-11 px-6"
            >
              {lang === "fr" ? "Voir toutes les offres" : "View All Jobs"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* CV Matcher */}
      <CVMatcher />

      {/* Latest jobs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="latest-jobs-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-[#1A8FE3] font-semibold">{lang === "fr" ? "Tendances" : "Trending"}</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-2">{lang === "fr" ? "Dernières offres" : "Latest jobs"}</h2>
          </div>
          <Button variant="ghost" onClick={() => navigate("/jobs")} data-testid="see-all-jobs" className="text-[#1A8FE3] hover:bg-sky-50">
            {lang === "fr" ? "Voir tout" : "See all"} <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {latest.map(j => <JobCard key={j.id} job={j} />)}
        </div>
        <div className="mt-10 flex justify-center">
          <Button
            onClick={() => navigate("/jobs")}
            data-testid="home-view-all-jobs"
            className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full h-12 px-8 shadow-md hover:shadow-lg transition-shadow"
          >
            {lang === "fr" ? "Voir toutes les offres" : "View All Jobs"} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Employer CTA */}
      <section className="bg-slate-50 py-20" data-testid="employer-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-[#1A8FE3] font-semibold">{lang === "fr" ? "Recruteurs" : "Employers"}</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-3">{lang === "fr" ? "Trouvez vos talents plus rapidement" : "Hire your next talent faster"}</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">{lang === "fr" ? "Publiez vos offres, recevez des candidatures qualifiées et gérez votre pipeline avec une interface pensée pour vous." : "Post your openings, receive qualified applications and manage your hiring pipeline with an interface built for you."}</p>
            <ul className="mt-6 space-y-3 text-slate-700">
              <li className="flex items-start gap-3"><Briefcase className="h-5 w-5 text-[#1A8FE3] mt-0.5" />Credit-based posting (30-day visibility)</li>
              <li className="flex items-start gap-3"><Users className="h-5 w-5 text-[#1A8FE3] mt-0.5" />Smart match scores + candidate summaries</li>
              <li className="flex items-start gap-3"><Star className="h-5 w-5 text-[#1A8FE3] mt-0.5" />Inbox to message candidates directly</li>
            </ul>
            <Button onClick={() => navigate("/register?role=employer")} data-testid="employer-cta-btn" className="mt-7 bg-black hover:bg-slate-800 text-white rounded-full px-6 h-12">
              {lang === "fr" ? "Créer mon compte recruteur" : "Create employer account"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          <div className="relative">
            <img src={EMP_BG} alt="" className="rounded-2xl shadow-xl object-cover w-full h-[420px]" />
            <div className="absolute -bottom-5 -left-5 bg-white border border-slate-200 rounded-2xl shadow-lg p-4 flex items-center gap-3">
              <Building2 className="h-8 w-8 text-[#1A8FE3]" />
              <div>
                <div className="font-display font-bold text-slate-900">200+</div>
                <div className="text-xs text-slate-500 uppercase tracking-widest">{lang === "fr" ? "entreprises" : "companies"}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats + Socials (moved to bottom) */}
      <StatsSection />
    </div>
  );
}
